import React from "react";
import { Link } from "react-router-dom";

/**
 * PageTopSlider / PageTopHero
 * Full-width top hero image banner for subpages.
 * Displays a single static background image seamlessly under the transparent navigation bar.
 */
export default function PageTopSlider({
  image,
  images = [],
  title,
  subtitle,
  badge,
  breadcrumbs = [],
  actions,
  priceBadge,
}) {
  // Extract a single static image from either `image` or `images[0]`
  const rawImage =
    image ||
    (Array.isArray(images) && images.length > 0 ? images[0] : null) || {
      url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg",
      alt: title || "ET Danmark ApS",
    };

  const selectedImage =
    typeof rawImage === "string"
      ? { url: rawImage, alt: title || "ET Danmark ApS" }
      : rawImage;

  const cleanPath = selectedImage.url.replace(
    /^https?:\/\/assets\.auxodev\.dk\/(cdn-cgi\/image\/[^/]+\/)?/,
    ""
  );

  const optimizedSrc = `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${cleanPath}`;
  const srcSet = `
    https://assets.auxodev.dk/cdn-cgi/image/width=640,quality=75,format=auto/${cleanPath} 640w,
    https://assets.auxodev.dk/cdn-cgi/image/width=1024,quality=80,format=auto/${cleanPath} 1024w,
    https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${cleanPath} 1600w,
    https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/${cleanPath} 1920w
  `;

  return (
    <section className="relative w-full overflow-hidden pt-36 sm:pt-40 lg:pt-44 pb-16 sm:pb-20 border-b border-stone-200/60 bg-stone-900">
      {/* Static Full-width Background Image */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          src={optimizedSrc}
          srcSet={srcSet}
          sizes="100vw"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          alt={selectedImage.alt || title || "ET Danmark"}
          className="w-full h-full object-cover object-center"
        />

        {/* Ambient Dark Overlay for contrast & transparent navbar integration */}
        <div className="absolute inset-0 z-1 bg-stone-950/45 backdrop-brightness-95 bg-gradient-to-r from-stone-950/85 via-stone-950/50 to-stone-950/40" />
      </div>

      {/* Foreground Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Breadcrumb Navigation */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav
            aria-label="Brødkrummesti"
            className="flex items-center gap-2 text-xs text-stone-300 mb-4 flex-wrap"
          >
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <span key={i} className="inline-flex items-center gap-2">
                  {i > 0 && (
                    <i
                      className="fa-solid fa-chevron-right text-[10px] text-stone-400"
                      aria-hidden="true"
                    ></i>
                  )}
                  {isLast ? (
                    <span
                      className="text-emerald-300 font-semibold tracking-wide"
                      aria-current="page"
                    >
                      {crumb.label}
                    </span>
                  ) : crumb.to ? (
                    <Link
                      to={crumb.to}
                      className="hover:text-white transition-colors underline-offset-2 hover:underline"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span>{crumb.label}</span>
                  )}
                </span>
              );
            })}
          </nav>
        )}

        <div className="max-w-3xl space-y-4">
          {/* Badge */}
          {badge && (
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-xs font-semibold uppercase tracking-wider backdrop-blur-xs">
              <span>{badge}</span>
            </div>
          )}

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight drop-shadow-xs">
            {title}
          </h1>

          {/* Subtitle / Description */}
          {subtitle && (
            <p className="text-stone-200 text-base sm:text-lg sm:leading-relaxed font-normal max-w-2xl drop-shadow-xs">
              {subtitle}
            </p>
          )}

          {/* Optional Price Badge & Action Buttons */}
          {(priceBadge || actions) && (
            <div className="pt-3 flex flex-wrap items-center gap-4">
              {priceBadge && (
                <div className="px-4 py-2 rounded-full bg-emerald-900/80 border border-emerald-500/30 text-emerald-200 text-sm font-semibold backdrop-blur-xs flex items-center gap-2 shadow-sm">
                  <i className="fa-solid fa-tag text-emerald-400" aria-hidden="true"></i>
                  <span>{priceBadge}</span>
                </div>
              )}
              {actions}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
